import React, { useState, useEffect } from 'react';
import {
  Container, Typography, Box, TextField, Button, Grid, Paper,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, Alert, CircularProgress,
} from '@mui/material';
import {
  Edit,
  Delete,
  ContentCopy,
  OpenInNew,
} from "@mui/icons-material";
import {
  collection,
  getDocs,
  doc,
  setDoc,
  deleteDoc,
  serverTimestamp,
} from "firebase/firestore";
import {
  firestore,
  storage,
  storageRef,
  uploadBytes,
  getDownloadURL,
} from "./firebase.config";
import { useAuth } from '../auth/AuthContext';
import { toast } from "react-toastify";

const TenantLandingManager = () => {
  const { currentUser, isSuperAdmin, loading: authLoading } = useAuth();
  const [pages, setPages] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ orgName: '', tagline: '', description: '', brandColor: '#7C3AED', logoUrl: '' });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (isSuperAdmin) fetchPages();
  }, [isSuperAdmin]);

  const fetchPages = async () => {
  // Get all tenants
  const tenantSnap = await getDocs(
    collection(firestore, "Tenants")
  );

  // Get all landing pages
  const landingSnap = await getDocs(
    collection(firestore, "TenantLandingPages")
  );

  // Convert landing pages into lookup
  const landingMap = {};

  landingSnap.forEach((doc) => {
    landingMap[doc.id] = doc.data();
  });

  const merged = tenantSnap.docs.map((doc) => {
    const tenant = doc.data();

    return {
      id: doc.id,

      tenantName:
        tenant.tenantName ||
        tenant.orgName ||
        "",

      ...(landingMap[doc.id] || {}),
    };
  });

  setPages(merged);
};

  const handleEdit = (page) => {
    setSelected(page);
    setForm({
      orgName: page.orgName || page.tenantName || "",
      tagline: page.tagline || '',
      description: page.description || '',
      brandColor: page.brandColor || '#7C3AED',
      logoUrl: page.logoUrl || '',
    });
  };

  const handleLogoUpload = async (file) => {
  if (!file || !selected) return;

  setUploading(true);

  try {
    const fileRef = storageRef(
      storage,
      `tenant-logos/${selected.id}/${Date.now()}_${file.name}`
    );

    await uploadBytes(fileRef, file);

    const url = await getDownloadURL(fileRef);

    setForm((prev) => ({
      ...prev,
      logoUrl: url,
    }));
  } catch (err) {
    console.error(err);
    toast.error("Logo upload failed.");
  } finally {
    setUploading(false);
  }
};

 const handleSave = async () => {
  if (!form.orgName.trim()) {
    toast.warning("Organization Name is required.");
    return;
  }

  if (!selected) return;

  setSaving(true);

  try {
    await setDoc(
  doc(firestore, "TenantLandingPages", selected.id),
  {
    ...form,
    updatedBy: currentUser.email,
    updatedAt: serverTimestamp(),
  },
  {
    merge: true,
  }
);

    toast.success("Landing page saved successfully.");

    setSelected(null);

    await fetchPages();
  } catch (err) {
    console.error(err);
    toast.error("Failed to save changes.");
  } finally {
    setSaving(false);
  }
};

  if (authLoading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  if (!isSuperAdmin) return <Container maxWidth="sm" sx={{ mt: 8 }}><Alert severity="error">SuperAdmin only.</Alert></Container>;


 const copyUrl = async (tenantId) => {
  const url = `${window.location.origin}/t/${tenantId}`;

  try {
    await navigator.clipboard.writeText(url);

    toast.success("URL copied successfully.");
  } catch {
    toast.error("Unable to copy URL.");
  }
};

const deleteLandingPage = async (tenantId) => {
  if (!window.confirm("Delete this tenant landing page?")) return;

  try {
    await deleteDoc(doc(firestore, "TenantLandingPages", tenantId));
await fetchPages();

    toast.success("Landing page deleted successfully.");
  } catch (err) {
    console.error(err);
    toast.error("Delete failed.");
  }
};

const sendUrl = (tenantId) => {
  const url = `${window.location.origin}/t/${tenantId}`;

  window.open(
    `mailto:?subject=Tenant Login Link&body=Please use this login link:%0A%0A${url}`
  );
};

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>Tenant Landing Pages</Typography>

      {selected && (
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>Editing: {selected.orgName}</Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Organization Name" value={form.orgName}
                onChange={(e) => setForm({ ...form, orgName: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Tagline / Subtitle" value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Page Description" value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })} />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Brand Color" type="color" value={form.brandColor}
                onChange={(e) => setForm({ ...form, brandColor: e.target.value })} />
            </Grid>
            <Grid item xs={12}>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 2,
    }}
  >
    <Button
      component="label"
      variant="outlined"
      disabled={uploading}
      sx={{
        height: 46,
        whiteSpace: "nowrap",
      }}
    >
      {uploading ? (
        <CircularProgress size={18} />
      ) : (
        "Upload Logo from Device"
      )}

      <input
        hidden
        type="file"
        accept="image/*"
        onChange={(e) => handleLogoUpload(e.target.files[0])}
      />
    </Button>

    {form.logoUrl && (
      <Box
        sx={{
          width: 64,
          height: 64,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          border: "1px solid #E0E0E0",
          borderRadius: 2,
          backgroundColor: "#fff",
          overflow: "hidden",
        }}
      >
        <Box
          component="img"
          src={form.logoUrl}
          alt="Logo Preview"
          sx={{
            maxWidth: "90%",
            maxHeight: "90%",
            objectFit: "contain",
          }}
        />
      </Box>
    )}
  </Box>
</Grid>
            <Grid item xs={12} sm={6}>
              <TextField fullWidth label="Logo URL" value={form.logoUrl}
                onChange={(e) => setForm({ ...form, logoUrl: e.target.value })}
                helperText="Paste an image URL (Google Drive share link, CDN, etc.)" />
            </Grid>
            <Grid item xs={12}>
              <Button variant="contained" disabled={saving || uploading} onClick={handleSave} sx={{ mr: 2 }}>
                {saving ? 'Saving...' : 'Save'}
              </Button>
              <Button variant="outlined" onClick={() => setSelected(null)}>Cancel</Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tenant ID</TableCell>
              <TableCell>Org Name</TableCell>
              <TableCell>Landing URL</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {pages.map((p) => (
              <TableRow key={p.id} sx={{
                "& td": {py: 1.2,},
                }}>

                <TableCell>
  <Box
    sx={{
      display: "inline-flex",
      alignItems: "center",
      px: 2,
      py: 0.5,
      borderRadius: "20px",
      border: "1px solid #E0E0E0",
      bgcolor: "#fff",
      fontSize: 13,
      fontFamily: "monospace",
      fontWeight: 600,
    }}
  >
    {p.id}
  </Box>
</TableCell>
                <TableCell>{p.orgName || p.tenantName}</TableCell>
                <TableCell>
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    <Typography
      component="a"
      href={`${window.location.origin}/t/${p.id}`}
      target="_blank"
      sx={{
        color: "#1976d2",
        textDecoration: "none",
        fontSize: 14,
      }}
    >
      /t/{p.id}
    </Typography>

    <Tooltip title="Copy URL">
      <IconButton
        size="small"
        onClick={() => copyUrl(p.id)}
      >
        <ContentCopy fontSize="small" />
      </IconButton>
    </Tooltip>

    <Tooltip title="Open">
      <IconButton
        size="small"
        component="a"
        href={`${window.location.origin}/t/${p.id}`}
        target="_blank"
      >
        <OpenInNew fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
</TableCell>
                <TableCell align="center">
  <Box
    sx={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      gap: 0.5,
    }}
  >
    <Tooltip title="Edit">
      <IconButton size="small" onClick={() => handleEdit(p)}>
        <Edit fontSize="small" />
      </IconButton>
    </Tooltip>

    <Tooltip title="Delete">
      <IconButton
        size="small"
        color="error"
        onClick={() => deleteLandingPage(p.id)}
      >
        <Delete fontSize="small" />
      </IconButton>
    </Tooltip>
  </Box>
</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Container>
  );
}
export default TenantLandingManager;